import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import EmployeeLayout from '../../components/layout/EmployeeLayout';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Package } from 'lucide-react';
import { formatUK } from '../../lib/ukTime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyKitPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchKit = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/kit`);
      setItems(res.data.items || []);
    } catch (e) {
      // silent — empty state covers it
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKit(); }, [fetchKit]);
  useAutoRefresh(fetchKit, { match: ['/kit'] });

  return (
    <EmployeeLayout>
      <div className="py-6 space-y-6">
        <div>
          <h1 className="text-2xl font-barlow tracking-widest uppercase flex items-center gap-2">
            <Package className="w-6 h-6 text-primary" strokeWidth={1.5} /> My Kit
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Equipment and uniform items currently issued to you. Returned items are removed automatically.
          </p>
        </div>

        {loading ? (
          <div className="py-8 text-center animate-pulse text-muted-foreground">Loading...</div>
        ) : items.length === 0 ? (
          <Card className="bg-card border-border rounded-sm" data-testid="my-kit-empty">
            <CardContent className="p-8 text-center space-y-2">
              <Package className="w-10 h-10 text-muted-foreground mx-auto" strokeWidth={1.25} />
              <p className="text-sm text-muted-foreground">No kit items issued to you at the moment.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="my-kit-list">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              {items.length} item{items.length === 1 ? '' : 's'} on you
            </p>
            {items.map((it) => (
              <Card key={it.id} className="bg-card border-border rounded-sm" data-testid={`my-kit-item-${it.id}`}>
                <CardContent className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-barlow uppercase tracking-wider text-base">{it.item_name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {it.size ? `Size ${it.size} · ` : ''}
                      Issued {it.issued_at ? formatUK(it.issued_at, 'd MMM yyyy') : '—'}
                      {it.issued_by_name ? ` by ${it.issued_by_name}` : ''}
                    </p>
                    {it.note && <p className="text-xs text-muted-foreground/80 mt-1 italic">{it.note}</p>}
                  </div>
                  <Badge className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary border-primary/40 shrink-0">
                    ×{it.quantity || 1}
                  </Badge>
                </CardContent>
              </Card>
            ))}
            <p className="text-[11px] text-muted-foreground pt-2">
              All issued items must be returned in good condition when requested or when your engagement ends.
            </p>
          </div>
        )}
      </div>
    </EmployeeLayout>
  );
}
